import type { StackProps, SwitchProps } from '@chakra-ui/react'
import { HStack, Switch } from '@chakra-ui/react'
import React from 'react'
import type { FormLabelProps } from '../FormLabel'
import { FormLabel } from '../FormLabel'
import { withFormControl, type FormFieldProps } from './hoc/withFormControl'

// Component-specific props
export interface SwitchFormControlComponentProps<TValue = any> {
  variant?: SwitchProps['variant']
  defaultChecked?: boolean
  children?: React.ReactNode
  checkedValue?: TValue
  uncheckedValue?: TValue
  size?: SwitchProps['size']
  gap?: StackProps['gap']
  switchLabel?: React.ReactNode
  switchLabelProps?: FormLabelProps
  tabIndex?: number
}

// Extended props that include HOC props
interface SwitchFormControlBaseProps extends SwitchFormControlComponentProps, FormFieldProps<boolean, HTMLInputElement> {
  // Props from HOC that we need to handle
  label?: React.ReactNode
  labelProps?: FormLabelProps
}

// Base component that handles switch logic
export const SwitchFormControlBase = React.forwardRef<
  HTMLInputElement,
  SwitchFormControlBaseProps
>((props, ref) => {
  const {
    // Form field props
    value,
    onChange,
    onBlur,
    isDisabled,
    name,
    // Component-specific props
    variant,
    size,
    defaultChecked,
    checkedValue = true,
    uncheckedValue = false,
    children,
    gap,
    switchLabel,
    switchLabelProps,
    // HOC props
    label,
    labelProps,
    tabIndex
  } = props

  const checked = (value === undefined || value === null) ? defaultChecked : value == checkedValue

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.checked ? checkedValue : uncheckedValue)
  }

  // Use HOC label if provided, otherwise fall back to switchLabel
  const labelToRender = label || switchLabel
  const labelPropsToUse = label ? labelProps : switchLabelProps

  return (
    <HStack gap={gap ?? 3} alignItems='center'>
      <Switch
        ref={ref}
        name={name}
        isChecked={checked}
        onBlur={onBlur}
        onChange={handleChange}
        isDisabled={isDisabled}
        variant={variant}
        value={checked ? '1' : ''}
        size={size}
        tabIndex={tabIndex}
      />
      {labelToRender && (
        <FormLabel fontWeight={checked ? 'semibold' : 'medium'} fontSize="md" {...labelPropsToUse}>
          {labelPropsToUse?.children ?? labelToRender}
        </FormLabel>
      )}
      {children}
    </HStack>
  )
})

;(SwitchFormControlBase as any).displayName = 'SwitchFormControlBase'

// Custom withFormControl for Switch that doesn't render label in FormControl
const withSwitchFormControl = <TComponentProps extends object>(
  Component: React.ComponentType<TComponentProps & FormFieldProps<boolean, HTMLInputElement>> | React.ForwardRefExoticComponent<Omit<TComponentProps & FormFieldProps<boolean, HTMLInputElement>, 'ref'> & React.RefAttributes<HTMLInputElement>>
) => {
  return withFormControl<TComponentProps, boolean, HTMLInputElement>(Component, {
    renderLabel: false // Pass label to component instead of rendering in FormControl
  })
}

// Export the wrapped component
export const SwitchFormControl = withSwitchFormControl<SwitchFormControlComponentProps>(SwitchFormControlBase)